// Browser PDF parser. Port of src/main/parsers/pdf.js to accept raw
// bytes instead of a file path, with pdfjs-dist lazy-loaded so the
// main bundle stays small — the user only pays the ~1MB pdfjs cost
// when they actually drop a PDF in.
//
// Classification algorithm (x-band → block type, scene-heading
// promotion, dual-dialogue prose demotion) is copied verbatim from the
// Electron version so a screenplay imported via web parses identically
// to the same file imported via Electron.

import {
  newScene,
  newBlock,
  recordCharacter,
  recordDialogue,
  characterKey,
  isSceneHeading,
} from './shared.js';

const PDF_X_BANDS = Object.freeze([
  { type: 'action', min: 0, max: 155 },
  { type: 'dialogue', min: 155, max: 210 },
  { type: 'parenthetical', min: 210, max: 240 },
  { type: 'character', min: 240, max: 360 },
  { type: 'transition', min: 360, max: 999 },
]);

function classifyByX(x) {
  for (const band of PDF_X_BANDS) {
    if (x >= band.min && x < band.max) return band.type;
  }
  return 'action';
}

function isPageHeaderOrFooter(line, pageTopY) {
  if (!line.text) return true;
  if (/^\s*\d{1,3}\.?\d*\.?\s*$/.test(line.text)) return true;
  if (/^\s*\(CONTINUED\)\s*$/i.test(line.text)) return true;
  if (/^\s*CONTINUED:\s*$/i.test(line.text)) return true;
  if (line.y > pageTopY - 50) return true;
  return false;
}

function flushLine(lines, parts, y) {
  if (!parts.length) return;
  let text = '';
  let lastEnd = null;
  const minX = Math.min(...parts.map((p) => p.x));
  for (const p of parts) {
    if (lastEnd !== null && p.x - lastEnd > 4 && !text.endsWith(' ')) text += ' ';
    text += p.str;
    lastEnd = p.x + p.str.length * (p.h * 0.5);
  }
  text = text.replace(/\s+/g, ' ').trim();
  if (text) lines.push({ y, x: minX, text });
}

// Lazy-loaded pdfjs + worker URL handed to it. Vite resolves the
// worker URL at build time and emits the worker JS as a separate
// asset; the main bundle doesn't include pdfjs until parsePDF runs.
let _pdfjsPromise = null;
async function loadPdfjs() {
  if (_pdfjsPromise) return _pdfjsPromise;
  _pdfjsPromise = (async () => {
    const [pdfjs, workerUrlModule] = await Promise.all([
      import('pdfjs-dist'),
      import('pdfjs-dist/build/pdf.worker.mjs?url'),
    ]);
    pdfjs.GlobalWorkerOptions.workerSrc = workerUrlModule.default;
    return pdfjs;
  })();
  return _pdfjsPromise;
}

export async function parsePDF(bytes) {
  if (!bytes) throw new Error('parsePDF: bytes required');
  const data =
    bytes instanceof Uint8Array
      ? bytes
      : new Uint8Array(bytes instanceof ArrayBuffer ? bytes : await bytes.arrayBuffer());

  const pdfjs = await loadPdfjs();
  let pdf;
  try {
    pdf = await pdfjs.getDocument({
      data,
      isEvalSupported: false,
      // disableFontFace governs rendering (which we never do); fine on.
      disableFontFace: true,
      verbosity: 0,
    }).promise;
  } catch (err) {
    const reason =
      err && err.name === 'PasswordException'
        ? 'The PDF is password-protected. Remove the password (e.g. macOS Preview ▸ Export, uncheck Encrypt) and try again.'
        : err && err.message
          ? err.message
          : 'Unknown PDF error.';
    throw new Error(`Could not read PDF: ${reason}`);
  }

  const Y_TOLERANCE = 2;
  const allLines = [];
  let title = null;
  const titlePage = {};

  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
    const page = await pdf.getPage(pageNum);
    const viewport = page.getViewport({ scale: 1 });
    const pageTopY = viewport.height;
    const tc = await page.getTextContent();

    const items = (tc.items || [])
      .filter((it) => it && typeof it.str === 'string' && it.str.length > 0)
      .map((it) => ({
        str: it.str,
        x: it.transform ? it.transform[4] : 0,
        y: it.transform ? it.transform[5] : 0,
        h: it.height || 12,
      }));

    items.sort((a, b) => {
      const dy = b.y - a.y;
      if (Math.abs(dy) > Y_TOLERANCE) return dy;
      return a.x - b.x;
    });

    const lines = [];
    let curY = null;
    let curParts = [];
    for (const it of items) {
      if (curY === null || Math.abs(it.y - curY) <= Y_TOLERANCE) {
        if (curY === null) curY = it.y;
        curParts.push(it);
      } else {
        flushLine(lines, curParts, curY);
        curParts = [it];
        curY = it.y;
      }
    }
    flushLine(lines, curParts, curY);

    const pageHasSceneHeading = lines.some((ln) => isSceneHeading(ln.text));
    if (pageNum === 1 && !pageHasSceneHeading) {
      const centered = [];
      for (const ln of lines) {
        if (ln.x > 200 && ln.x < 400 && ln.text.trim().length > 0 && ln.text.length < 80) {
          centered.push(ln.text.trim());
        }
      }
      if (centered.length > 0 && !title) title = centered[0];
      if (centered.length > 1) {
        titlePage.title = title || centered[0];
        const second = centered[1];
        if (/^(written|story|screenplay|teleplay|adaptation|by\b)/i.test(second)) {
          titlePage.credit = second;
          if (centered.length > 2) titlePage.author = centered[2];
        } else {
          titlePage.author = second;
          if (centered.length > 2) titlePage.source = centered[2];
        }
      } else if (title) {
        titlePage.title = title;
      }
      continue;
    }

    for (const ln of lines) {
      if (isPageHeaderOrFooter(ln, pageTopY)) continue;
      allLines.push({ ...ln, page: pageNum });
    }
  }

  const typicalGap = 12;
  const blockGapThreshold = typicalGap * 1.5;

  const rawBlocks = [];
  let cur = null;
  let lastY = null;
  let lastPage = null;
  for (const ln of allLines) {
    const type = classifyByX(ln.x);
    const isNewPage = lastPage !== null && ln.page !== lastPage;
    const gap = lastY !== null && !isNewPage ? lastY - ln.y : Infinity;
    const startsNewBlock = !cur || cur.type !== type || gap > blockGapThreshold || isNewPage;

    if (startsNewBlock) {
      if (cur) rawBlocks.push(cur);
      cur = { type, text: ln.text };
    } else {
      cur.text = (cur.text + ' ' + ln.text).replace(/\s+/g, ' ').trim();
    }
    lastY = ln.y;
    lastPage = ln.page;
  }
  if (cur) rawBlocks.push(cur);

  for (const b of rawBlocks) {
    if (b.type === 'action') {
      if (isSceneHeading(b.text)) b.type = 'scene-heading';
      else if (/^[A-Z\s]+TO:\s*$/.test(b.text)) b.type = 'transition';
    }
    if (b.type === 'parenthetical') {
      if (!/^\(/.test(b.text)) b.text = `(${b.text.replace(/^\(|\)$/g, '').trim()})`;
    }
    if (b.type === 'character') {
      const looksLikeProse = b.text.length > 40 || /[a-z]/.test(b.text) || /[.?!]\s*$/.test(b.text);
      if (looksLikeProse) b.type = 'dialogue';
    }
  }

  const scenes = [];
  const prelude = [];
  const characters = {};
  let currentScene = null;
  let sceneNumber = 0;
  let lastCharacter = null;

  for (const b of rawBlocks) {
    if (b.type === 'scene-heading') {
      sceneNumber += 1;
      currentScene = newScene(sceneNumber, b.text);
      scenes.push(currentScene);
      lastCharacter = null;
      continue;
    }
    if (!currentScene) {
      prelude.push(newBlock(b.type, b.text));
      if (b.type === 'character') {
        lastCharacter = characterKey(b.text);
        recordCharacter(characters, lastCharacter, null);
      } else if (b.type === 'dialogue' && lastCharacter) {
        recordDialogue(characters, lastCharacter, b.text);
      } else if (b.type === 'action' || b.type === 'transition') {
        lastCharacter = null;
      }
      continue;
    }
    currentScene.blocks.push(newBlock(b.type, b.text));

    if (b.type === 'character') {
      lastCharacter = characterKey(b.text);
      recordCharacter(characters, lastCharacter, currentScene);
    } else if (b.type === 'dialogue' && lastCharacter) {
      recordDialogue(characters, lastCharacter, b.text);
    } else if (b.type === 'action' || b.type === 'transition') {
      lastCharacter = null;
    }
  }

  if (allLines.length === 0 && pdf.numPages > 0) {
    throw new Error(
      'No extractable text in the PDF. This usually means the file is ' +
        'a scanned image — apply OCR first (Preview ▸ Export ▸ "Apply OCR"), ' +
        'or open the original .fdx / .fountain file instead.',
    );
  }
  if (scenes.length === 0 && rawBlocks.length > 0) {
    prelude.length = 0;
    for (const b of rawBlocks) {
      prelude.push(newBlock('action', b.text));
    }
  }

  return { title: title || 'Untitled', titlePage, prelude, scenes, characters };
}

export { classifyByX, PDF_X_BANDS };
